import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Search, User, LogOut, Settings, UserCircle, ChevronDown } from 'lucide-react';
import { useUser } from '../../context/UserContext';

const AdminNavbar = ({ sidebarCollapsed }) => {
  const navigate = useNavigate();
  const { user, logout } = useUser();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <header 
      className="admin-navbar position-fixed top-0 bg-white border-bottom shadow-sm"
      style={{
        left: sidebarCollapsed ? '70px' : '260px',
        right: 0,
        zIndex: 1030,
        height: '70px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.9) !important'
      }}
    >
      <div className="d-flex align-items-center justify-content-between h-100 px-4">
        
        {/* Modern Search Bar */}
        <div className="d-none d-md-block" style={{ width: '350px' }}>
          <div className="input-group input-group-merge bg-light rounded-pill px-3">
            <span className="input-group-text bg-transparent border-0 pe-2">
              <Search size={18} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control bg-transparent border-0 ps-0 py-2 shadow-none"
              placeholder="Search analytics, orders..."
              style={{ fontSize: '0.9rem' }}
            />
            <span className="input-group-text bg-transparent border-0 text-muted small">
              <kbd className="bg-white border text-muted fw-normal">⌘K</kbd>
            </span>
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="d-flex align-items-center gap-2">
          
          {/* Notifications Dropdown */}
          <div className="dropdown">
            <button 
              className="btn btn-icon btn-light rounded-circle position-relative border-0 shadow-none" 
              data-bs-toggle="dropdown"
              style={{ width: '40px', height: '40px' }}
            >
              <Bell size={20} className="text-secondary" />
              <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-2 border-white rounded-circle">
                <span className="visually-hidden">New alerts</span>
              </span>
            </button>
            
            <div className="dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 mt-3 py-0 overflow-hidden" style={{ width: '320px' }}>
              <div className="px-3 py-3 bg-primary text-white">
                <h6 className="mb-0">Notifications</h6>
                <small className="opacity-75">You have 3 unread messages</small>
              </div>
              <div className="list-group list-group-flush">
                <button className="list-group-item list-group-item-action p-3 border-0">
                  <div className="d-flex gap-3">
                    <div className="bg-success-subtle text-success p-2 rounded-3 h-100">
                      <User size={16} />
                    </div>
                    <div>
                      <p className="mb-1 small fw-bold">New Seller Registered</p>
                      <small className="text-muted">2 mins ago</small>
                    </div>
                  </div>
                </button>
                {/* Add more items here */}
              </div>
              <Link to="/admin/notifications" className="dropdown-item text-center py-2 small text-primary border-top fw-medium">
                View all notifications
              </Link>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="vr mx-2 text-black-50 opacity-25" style={{ height: '24px' }}></div>

          {/* Profile Dropdown */}
          <div className="dropdown">
            <button 
              className="btn btn-link text-decoration-none p-1 d-flex align-items-center gap-2 border-0 shadow-none profile-trigger"
              data-bs-toggle="dropdown"
            >
              <div className="position-relative">
                <img 
                  src={`https://ui-avatars.com/api/?name=${user?.name || 'Admin'}&background=0d6efd&color=fff`} 
                  alt="avatar" 
                  className="rounded-circle"
                  style={{ width: '38px', height: '38px', objectFit: 'cover' }}
                />
                <span className="position-absolute bottom-0 end-0 p-1 bg-success border border-2 border-white rounded-circle"></span>
              </div>
              <div className="text-start d-none d-lg-block me-1">
                <p className="fw-bold small mb-0 lh-1 text-dark">{user?.name || 'Admin User'}</p>
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>Super Admin</small>
              </div>
              <ChevronDown size={14} className="text-muted d-none d-lg-block" />
            </button>

            <ul className="dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 mt-3 p-2" style={{ minWidth: '220px' }}>
              <li>
                <div className="px-3 py-2 mb-2 bg-light rounded-3">
                  <p className="small text-muted mb-0 lh-1">Signed in as</p>
                  <p className="small fw-bold mb-0 text-truncate">{user?.email || 'admin@paudhawala.com'}</p>
                </div>
              </li>
              <li>
                <Link className="dropdown-item rounded-3 py-2 d-flex align-items-center gap-2" to="/admin/profile">
                  <UserCircle size={18} /> Profile
                </Link>
              </li>
              <li>
                <Link className="dropdown-item rounded-3 py-2 d-flex align-items-center gap-2" to="/admin/settings">
                  <Settings size={18} /> Settings
                </Link>
              </li>
              <li><hr className="dropdown-divider opacity-50" /></li>
              <li>
                <button onClick={handleLogout} className="dropdown-item rounded-3 py-2 text-danger d-flex align-items-center gap-2">
                  <LogOut size={18} /> Logout
                </button>
              </li>
            </ul>
          </div>

        </div>
      </div>

      <style>{`
        .admin-navbar .form-control:focus {
          background-color: #fff !important;
          border-color: #0d6efd !important;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.1) !important;
        }
        .btn-icon:hover {
          background-color: #e9ecef;
          transform: translateY(-1px);
        }
        .dropdown-item:active {
          background-color: #0d6efd;
        }
        .profile-trigger:hover img {
          filter: brightness(0.9);
        }
        kbd {
          font-family: system-ui;
          font-size: 0.7rem;
        }
      `}</style>
    </header>
  );
};

export default AdminNavbar;